import { cx } from '@vizit/theme';
import * as React from 'react';
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
  TooltipContext,
  composeRenderProps,
} from 'react-aria-components';
import { RadialProgress } from '../radial-progress';
import { buttonClasses } from './styles';
import { ButtonLevel, ButtonSize } from './types';

interface ButtonProps extends AriaButtonProps {
  /**
   * Whether the button is fully rounded (pill shape)
   *
   * @optional
   * @default false
   * @type boolean
   */
  rounded?: boolean;
  /**
   * Whether the button is loading
   *
   * @optional
   * @default false
   * @type boolean
   */
  isLoading?: boolean;
  /**
   * The variant of the button
   *
   * @optional
   * @default 'primary'
   * @type 'primary' | 'secondary' | 'ghost' | 'text-primary' | 'text-secondary' | 'gradient';
   */
  level?: ButtonLevel;
  /**
   * The size of the button
   *
   * @optional
   * @default 'lg'
   * @type 'xl' | 'lg' | 'md' | 'sm' | 'xs'
   */
  size?: ButtonSize;
  /**
   * The left icon of the button
   *
   * @optional
   * @type React.ReactNode
   */
  iconStart?: React.ReactNode;
  /**
   * The right icon of the button
   *
   * @optional
   * @type React.ReactNode
   */
  iconEnd?: React.ReactNode;
}

/**
 * The Button component is used to create a button.
 *
 * @param props - The props for the Button component.
 * @example
 * <Button level="primary" size="sm">
 *   Button Text
 * </Button>
 *
 * Buttons that are only icons should have an aria-label prop.
 * <Button level="ghost" size="sm" iconStart={<SearchSmallIcon size={20} aria-label="Search" />} />
 */
function Button(props: ButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) {
  const {
    'aria-label': ariaLabel,
    children,
    className: classNameProp,
    isDisabled = false,
    isLoading = false,
    level = ButtonLevel.Primary,
    size = ButtonSize.Sm,
    iconStart,
    rounded = false,
    iconEnd,
    ...other
  } = props;

  const isIconOnly = !!(iconStart || iconEnd) && !children;

  const hasTooltip = !!React.useContext(TooltipContext);
  // If there is a tooltip, we need to simulate a disabled state so pointer events are not blocked
  const disabledProps = hasTooltip
    ? { isDisabled: isLoading, ...(isDisabled ? { onPress: () => {} } : {}) }
    : { isDisabled: isDisabled || isLoading };

  return (
    <AriaButton
      {...other}
      aria-label={isLoading ? 'Loading' : ariaLabel}
      ref={ref}
      className={composeRenderProps(classNameProp, (className, renderProps) =>
        cx(
          'button group',
          buttonClasses({ ...renderProps, level, size, isDisabled, isLoading, isIconOnly, rounded }),
          hasTooltip && '!pointer-events-auto',
          className,
        ),
      )}
      {...disabledProps}
    >
      {(value) => (
        <>
          {isLoading && <RadialProgress isIndeterminate fillClassName="stroke-white" size={8} aria-label="Loading" />}
          {!isLoading && iconStart && iconStart}
          {!isLoading && (typeof children === 'function' ? children(value) : children)}
          {!isLoading && iconEnd && iconEnd}
        </>
      )}
    </AriaButton>
  );
}

const _Button = React.forwardRef<HTMLButtonElement, ButtonProps>(Button);

export { _Button as Button };
export type { ButtonProps };
